import {
  Button,
  ButtonGroup,
  Input,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { useForm, type SubmitHandler } from "react-hook-form"
import { FaPlus } from "react-icons/fa"
import {
  DialogActionTrigger,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog"
import { Field } from "../ui/field"
import { ReportsService, type FeedCreate } from "@/client"
import type { ApiError } from "../../client/core/ApiError"
import useCustomToast from "../../hooks/useCustomToast"
import { handleError } from "../../utils"

const AddFeed = () => {
  const queryClient = useQueryClient()
  const { showSuccessToast } = useCustomToast()
  const [isOpen, setIsOpen] = useState(false)
  const [hasSubmitted, setHasSubmitted] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid, isSubmitting },
  } = useForm<FeedCreate>({
    mode: "onBlur",
    criteriaMode: "all",
    defaultValues: {
      feed_name: "",
      feed_description: "",
      feed_url: "",
    },
  })

  const mutation = useMutation({
    mutationFn: (data: FeedCreate) => ReportsService.createFeed({ requestBody: data }),
    onSuccess: () => {
      showSuccessToast("RSS 피드가 성공적으로 추가되었습니다.")
      queryClient.invalidateQueries({ queryKey: ["feedMetadata"] })
      reset()
      setHasSubmitted(false)
      setIsOpen(false)
    },
    onError: (err: ApiError) => {
      handleError(err)
      setHasSubmitted(false)
    },
  })

  const onSubmit: SubmitHandler<FeedCreate> = (data) => {
    if (hasSubmitted) return
    setHasSubmitted(true)
    mutation.mutate(data)
  }

  return (
    <DialogRoot
      size={{ base: "xs", md: "md" }}
      placement="center"
      open={isOpen}
      onOpenChange={({ open }) => setIsOpen(open)}
    >
      <DialogTrigger asChild>
        <Button value="add-feed" mb={4}>
          <FaPlus fontSize="16px" />
          피드 추가
        </Button>
      </DialogTrigger>

      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogHeader>
            <DialogTitle>RSS 피드 추가</DialogTitle>
          </DialogHeader>

          <DialogBody>
            <Text mb={4}>새 RSS 피드를 추가하려면 세부 정보를 입력하세요.</Text>
            <VStack gap={4}>
              <Field
                required
                invalid={!!errors.feed_name}
                errorText={errors.feed_name?.message}
                label="피드 이름"
              >
                <Input
                  id="feed_name"
                  {...register("feed_name", { required: "피드 이름은 필수입니다." })}
                  placeholder="예: 환경 뉴스 피드"
                />
              </Field>

              <Field
                required
                invalid={!!errors.feed_description}
                errorText={errors.feed_description?.message}
                label="피드 설명"
              >
                <Input
                  id="feed_description"
                  {...register("feed_description", { required: "피드 설명은 필수입니다." })}
                  placeholder="피드 간단 설명"
                />
              </Field>

              <Field
                required
                invalid={!!errors.feed_url}
                errorText={errors.feed_url?.message}
                label="RSS URL"
              >
                <Input
                  id="feed_url"
                  {...register("feed_url", {
                    required: "RSS URL은 필수입니다.",
                    pattern: {
                      value: /^https?:\/\/.+/,
                      message: "유효한 URL 형식이어야 합니다.",
                    },
                  })}
                  placeholder="https://example.com/rss"
                  type="url"
                />
              </Field>
            </VStack>
          </DialogBody>

          <DialogFooter gap={2}>
            <ButtonGroup>
              <DialogActionTrigger asChild>
                <Button variant="subtle" colorPalette="gray" disabled={isSubmitting}>
                  취소
                </Button>
              </DialogActionTrigger>
              <Button
                type="submit"
                variant="solid"
                disabled={!isValid || isSubmitting || hasSubmitted}
                loading={isSubmitting}
              >
                저장
              </Button>
            </ButtonGroup>
          </DialogFooter>
        </form>
        <DialogCloseTrigger />
      </DialogContent>
    </DialogRoot>
  )
}

export default AddFeed
